import React, { useEffect, useMemo, useState } from "react";
import { API_URL, FRONTEND_URL } from "../config";
import { backendErrorMessage, readJsonResponse } from "../apiDiagnostics";
import PageShell from "../components/PageShell";

function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("admin-key") || "");
  const [lists, setLists] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const totals = useMemo(() => {
    return lists.reduce(
      (sum, list) => {
        const listItems = list.items || [];
        const assignments = listItems.reduce((count, item) => count + (item.assignees || []).length, 0);

        return {
          lists: sum.lists + 1,
          items: sum.items + listItems.length,
          assignments: sum.assignments + assignments
        };
      },
      { lists: 0, items: 0, assignments: 0 }
    );
  }, [lists]);

  async function loadAdminLists(nextAdminKey = adminKey) {
    const cleanAdminKey = nextAdminKey.trim();

    if (!cleanAdminKey) {
      setError("Bitte Admin-Key eingeben.");
      return;
    }

    const url = `${API_URL}/api/admin/lists`;
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${cleanAdminKey}`
        }
      });
      const data = await readJsonResponse(response, "Adminlisten laden", url);

      if (!response.ok) {
        setLists([]);
        setHasLoaded(false);
        setError(data.error || "Adminlisten konnten nicht geladen werden.");
        return;
      }

      sessionStorage.setItem("admin-key", cleanAdminKey);
      setAdminKey(cleanAdminKey);
      setLists(data.lists || []);
      setHasLoaded(true);
    } catch (err) {
      setLists([]);
      setHasLoaded(false);
      setError(backendErrorMessage("Adminlisten laden", url, err));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (adminKey) {
      loadAdminLists(adminKey);
    }
  }, []);

  function submit(event) {
    event.preventDefault();
    loadAdminLists();
  }

  function logout() {
    sessionStorage.removeItem("admin-key");
    setAdminKey("");
    setLists([]);
    setHasLoaded(false);
    setError("");
  }

  return (
    <PageShell maxWidth="max-w-6xl">
      <section className="mb-8 border-b border-[#dce8c8] pb-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">Admin</p>
            <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">Alle Listen</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-neutral-600">
              Geschützte Übersicht über erstellte Listen, Einträge und Namen.
            </p>
          </div>

          <form className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-[360px] sm:flex-row" onSubmit={submit}>
            <input
              className="min-h-[48px] flex-1 border border-[#c9d9b4] bg-white px-4 text-sm shadow-sm"
              type="password"
              value={adminKey}
              onChange={event => setAdminKey(event.target.value)}
              placeholder="Admin-Key"
              autoComplete="current-password"
            />
            <button
              className="min-h-[48px] border border-[#172017] bg-[#172017] px-5 text-sm font-medium text-white shadow-sm hover:bg-[#263826] disabled:cursor-not-allowed disabled:opacity-60"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Laedt..." : "Anzeigen"}
            </button>
          </form>
        </div>
      </section>

      {error && (
        <div className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      {hasLoaded && (
        <>
          <section className="mb-6 grid gap-3 sm:grid-cols-3">
            <div className="border border-[#dce8c8] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-500">Listen</p>
              <p className="mt-2 text-3xl font-semibold">{totals.lists}</p>
            </div>
            <div className="border border-[#dce8c8] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-500">Einträge</p>
              <p className="mt-2 text-3xl font-semibold">{totals.items}</p>
            </div>
            <div className="border border-[#dce8c8] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-500">Namen</p>
              <p className="mt-2 text-3xl font-semibold">{totals.assignments}</p>
            </div>
          </section>

          <div className="mb-5 flex justify-end">
            <button
              className="border border-[#172017] bg-white px-5 py-3 text-sm font-medium text-[#172017] shadow-sm hover:bg-[#f1f5e8]"
              type="button"
              onClick={logout}
            >
              Admin-Key entfernen
            </button>
          </div>

          <section className="space-y-5">
            {lists.length === 0 ? (
              <div className="border border-[#dce8c8] bg-white p-6 text-sm text-neutral-600 shadow-sm">
                Keine Listen vorhanden.
              </div>
            ) : (
              lists.map(list => (
                <article className="border border-[#dce8c8] bg-white p-5 shadow-sm" key={list.id}>
                  <div className="flex flex-col gap-4 border-b border-[#edf3e2] pb-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold tracking-tight">{list.title}</h2>
                      <p className="mt-2 text-sm text-neutral-500">
                        {list.listType === "ordnung" ? "Ordnungsliste" : "Mitbringliste"} - {list.closed ? "geschlossen" : "offen"} - ID {list.id}
                      </p>
                      <p className="mt-1 text-sm text-neutral-500">
                        Erstellt: {list.createdAt ? new Date(list.createdAt).toLocaleString("de-DE") : "unbekannt"}
                      </p>
                    </div>
                    <a
                      className="w-fit border border-[#172017] bg-white px-4 py-2 text-sm font-medium text-[#172017] shadow-sm hover:bg-[#f1f5e8]"
                      href={`${FRONTEND_URL}/liste/${list.id}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Schülerlink öffnen
                    </a>
                  </div>

                  <div className="mt-4 space-y-3">
                    {(list.items || []).map(item => {
                      const assignees = item.assignees || [];
                      const usedAmount = assignees.reduce((sum, person) => sum + Number(person.quantity || 1), 0);

                      return (
                        <div className="border border-[#edf3e2] bg-[#fbfcf8] p-4" key={item.id}>
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                            <h3 className="text-lg font-semibold">{item.name}</h3>
                            <p className="text-sm text-neutral-500">{usedAmount} von {item.max_count} belegt</p>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            {assignees.length === 0 ? (
                              <p className="text-sm text-neutral-500">Noch niemand eingetragen.</p>
                            ) : (
                              assignees.map(person => (
                                <span className="border border-[#dce8c8] bg-white px-3 py-2 text-sm font-medium text-neutral-700" key={person.id}>
                                  {person.name}{list.listType !== "ordnung" && <> - {person.quantity}x</>}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>
              ))
            )}
          </section>
        </>
      )}
    </PageShell>
  );
}

export default AdminPage;
